/**
 * useRoles Hook
 *
 * React Query hook for fetching roles list.
 * Implements caching with 5-minute stale time.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Use useRoles() to get all tenant roles
 * - Use useRolesByApplication() to get roles with permissions for a specific app
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { roleService } from '../../../api/role.service';
import type { RoleSanitized, RoleListQuery } from '../../../types/role.types';

/**
 * Fetch all global roles for a tenant
 *
 * @param tenantId - Tenant ID
 * @param query - Optional query parameters for filtering
 * @returns Query result with roles array
 */
export const useRoles = (
  tenantId: string,
  query?: RoleListQuery
): UseQueryResult<RoleSanitized[], Error> => {
  return useQuery<RoleSanitized[], Error>({
    queryKey: ['roles', tenantId, query],
    queryFn: async () => {
      return await roleService.getRoles(tenantId, query);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch roles that have permissions for a specific application
 *
 * @param tenantId - Tenant ID
 * @param applicationId - Application ID to filter by
 * @param query - Optional query parameters for filtering
 * @returns Query result with roles array
 */
export const useRolesByApplication = (
  tenantId: string,
  applicationId: string,
  query?: RoleListQuery
): UseQueryResult<RoleSanitized[], Error> => {
  return useQuery<RoleSanitized[], Error>({
    queryKey: ['roles', tenantId, 'application', applicationId, query],
    queryFn: async () => {
      return await roleService.getRolesByApplication(tenantId, applicationId, query);
    },
    enabled: !!tenantId && !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
